import { CdkAccordionItem, CdkAccordionModule } from '@angular/cdk/accordion';
import { DatePipe, JsonPipe, Location, ViewportScroller } from '@angular/common';
import { AfterViewInit, Component, Input, Signal, WritableSignal, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { AratChartComponent } from '../../../components/arat-chart/arat-chart.component';
import { BarChartComponent } from '../../../components/bar-chart/bar-chart.component';
import { ChartBaseComponent } from '../../../components/chart-base/chart-base.component';
import { DoubleBarChartComponent } from '../../../components/double-bar-chart/double-bar-chart.component';
import { ExampleChartComponent } from '../../../components/example-chart/example-chart.component';
import { IcfBarPercentComponent } from '../../../components/icf-bar-percent/icf-bar-percent.component';
import { IcfBarComponent } from '../../../components/icf-bar/icf-bar.component';
import { LineChartMultiComponent } from '../../../components/line-chart-multi/line-chart-multi.component';
import { LineChartNhptBarsComponent } from '../../../components/line-chart-nhpt-bars/line-chart-nhpt-bars.component';
import { LineChartNhptComponent } from '../../../components/line-chart-nhpt/line-chart-nhpt.component';
import { LineChartPercentComponent } from '../../../components/line-chart-percent/line-chart-percent.component';
import { LineChartSingleComponent } from '../../../components/line-chart-single/line-chart-single.component';
import { LineChartComponent } from '../../../components/line-chart/line-chart.component';
import { SingleBarChartComponent } from '../../../components/single-bar-chart/single-bar-chart.component';
import { AssessmentsService } from '../../../services/assessments.service';
import {
  Assessment,
  AssessmentChartDataRow,
  AssessmentChartDataRowMixed,
  AssessmentChartDataSetCustom,
  AssessmentChartDataSets,
  AssessmentType,
} from '../../../services/model';
import { PatientsService } from '../../../services/patients.service';
import { AssessmentTableComponent } from '../graph/assessment-table/assessment-table.component';
import { DateRelativPipe } from '../../../pipes/datum-relativ.pipe';
import { DetailsComponent } from './details/details.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-graph-new',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    ChartBaseComponent,
    LineChartComponent,
    LineChartPercentComponent,
    BarChartComponent,
    AratChartComponent,
    SingleBarChartComponent,
    DoubleBarChartComponent,
    AssessmentTableComponent,
    JsonPipe,
    ExampleChartComponent,
    LineChartSingleComponent,
    LineChartNhptComponent,
    LineChartNhptBarsComponent,
    LineChartMultiComponent,
    CdkAccordionModule,
    IcfBarComponent,
    DatePipe,
    IcfBarPercentComponent,
    DateRelativPipe,
    DetailsComponent,
  ],
  templateUrl: './graph-new.component.html',
  styleUrl: './graph-new.component.scss',
})
export class GraphNewComponent implements AfterViewInit {
  @Input() patientId?: string;

  private location: Location = inject(Location);
  private scroller: ViewportScroller = inject(ViewportScroller);
  private assessmentsService: AssessmentsService = inject(AssessmentsService);
  public patientsService: PatientsService = inject(PatientsService);
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);

  assessmentList: WritableSignal<Assessment[]> = this.assessmentsService.assessmentList;

  assessmentChartDataSets: Signal<AssessmentChartDataSets | undefined> =
    this.assessmentsService.assessmentChartDataSets.asReadonly();
  assessmentChartDataSetsCustom: Signal<AssessmentChartDataSetCustom[]> =
    this.assessmentsService.assessmentChartDataSetsCustom.asReadonly();

  recentAssessmentChartDataSets: Signal<AssessmentChartDataRow[]> = computed(() => {
    const rows: AssessmentChartDataRow[] = [];
    const datasetsObject: AssessmentChartDataSets = this.assessmentChartDataSets() ?? {};
    const keys: Array<AssessmentType> = Object.keys(datasetsObject) as Array<AssessmentType>;
    const keysSorted: Array<AssessmentType> = keys.sort();
    keysSorted.forEach((type: AssessmentType) => {
      const perType: AssessmentChartDataRow[] | undefined = datasetsObject[type];
      if (perType) {
        const row = perType[0];
        rows.push(row);
      }
    });
    // Object.keys(datasets).forEach((key: AssessmentType) => {
    //   const perType: AssessmentChartDataRow[] | undefined = datasets[key];
    //   const row = perType[0];
    //   rows.push();
    // });
    // console.log('rows: ', rows);

    return rows;
  });
  recentAssessmentChartDataSetsNoCustom: Signal<AssessmentChartDataRow[]> = computed(() => {
    return this.recentAssessmentChartDataSets().filter(item => item.type !== 'CUSTOM');
  });

  recentAssessmentChartDataSetsMixed: Signal<AssessmentChartDataRowMixed[]> = computed(() => {
    const mixed: AssessmentChartDataRowMixed[] = [];
    this.recentAssessmentChartDataSetsNoCustom().forEach(item => mixed.push({ standard: item }));
    this.assessmentChartDataSetsCustom().forEach(item => mixed.push({ custom: item }));
    return mixed.sort((a, b) => {
      const nameA: string = a.standard ? a.standard.type : a.custom!.customAssessment.shortName;
      const nameB: string = b.standard ? b.standard.type : b.custom!.customAssessment.shortName;
      return nameA.localeCompare(nameB);
    });
  });

  initiallyOpenedAssessmentType: AssessmentType | undefined;
  initiallyOpenedCustomId: number | undefined;

  $routeParams = toSignal(this.route.queryParamMap);

  constructor() {
    // console.log('chris constructor GraphComponent', this.$routeParams());
    const assessmentType: AssessmentType | undefined = this.$routeParams()?.get('type') as AssessmentType;
    const customId: number = Number.parseFloat(this.$routeParams()?.get('cId') ?? '-1');
    if (assessmentType) {
      this.initiallyOpenedAssessmentType = assessmentType;
    } else if (customId) {
      this.initiallyOpenedCustomId = customId;
    }
    // console.log('chris constructor GraphComponent ' + this.initiallyOpenedAssessmentType);
  }

  ngAfterViewInit(): void {
    if (this.initiallyOpenedAssessmentType) {
      this.scroller.scrollToAnchor(this.initiallyOpenedAssessmentType);
    } else if (this.initiallyOpenedCustomId) {
      this.scroller.scrollToAnchor('custom' + this.initiallyOpenedCustomId);
    }
  }
  newAssessment() {
    this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
  }

  newAssessmentOfType(type: string) {
    this.router.navigate(['../../assessments/' + type], { relativeTo: this.route });
  }

  navigateToAssessment(assessment: Assessment) {
    // console.log('ass', assessment);
    if (assessment) {
      this.router.navigate(['../../assessments/' + assessment.type.toLowerCase() + '/' + assessment._documentId], {
        relativeTo: this.route,
      });
    }
  }

  // von  reports/assessments
  // nach assessments/arat
  onArat() {
    this.router.navigate(['../../assessments/arat'], { relativeTo: this.route });
  }

  onIndexClicked(index: number, appDetails: DetailsComponent): void {
    // console.log('chris onIndexClicked ' + index, appDetails);
    appDetails.assessmentIndex.set(index);
    // const assessment: Assessment = appDetails.currentAssessment();
    // if (assessment.customId) {
    //   this.updateUrlParams({ cId: assessment.customId.toString(), dId: assessment._documentId! });
    // } else {
    //   this.updateUrlParams({ type: assessment.type, dId: assessment._documentId! });
    // }
  }

  updateUrlParams(params: { [key: string]: string }) {
    // Hole die aktuelle URL ohne Parameter
    // const path = this.route.snapshot.routeConfig?.path;
    const fullPath = this.router.url.split('?')[0]; // Nur den Pfad ohne bestehende Query-Parameter

    // Erzeuge einen neuen Query-String
    const queryParams = new URLSearchParams(params).toString();

    // Update die URL mit den neuen Parametern
    this.location.replaceState(fullPath ?? '', queryParams);
  }

  detailsEinOderAusblenden(accordionItem: CdkAccordionItem, mixedItem: AssessmentChartDataRowMixed): void {
    // console.log('detailsEinOderAusblenden ' + accordionItem.expanded, mixedItem);
    let params = {};
    if (mixedItem.standard) {
      params = { type: mixedItem.standard.type };
      if (this.initiallyOpenedAssessmentType === mixedItem.standard.type) {
        accordionItem.expanded = true; // ist ja noch alles false initial
        this.initiallyOpenedAssessmentType = undefined;
      }
    } else if (mixedItem.custom) {
      const customId: number = mixedItem.custom.customAssessment.id!;
      params = { cId: customId.toString() };
      if (this.initiallyOpenedCustomId === customId) {
        accordionItem.expanded = true; // ist ja noch alles false initial
        this.initiallyOpenedCustomId = undefined;
      }
    }
    if (accordionItem.expanded) {
      // wenn schon eingeblendet war, dann mal alles zurücksetzen
      this.updateUrlParams({});
    } else {
      this.updateUrlParams(params);
    }

    accordionItem.toggle();
  }
}
